import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import PropTypes from 'prop-types';
import {Map} from '@vis.gl/react-google-maps';
import {useSyncSitesToIndexedDb} from '@/hooks/useSyncSitesToIndexedDb';
import {AnimatePresence, motion} from "framer-motion";
import {ClusteredMarkerLayer} from "./ClusteredMarkerLayer";
import DropdownFilter from '@/components/DropdownFilter';
import SiteEngagements from "./SiteEngagements";
const DEBUG_KEY = 'communityshare-debug'

ProgressBar.propTypes = {
  percent: PropTypes.number,
}
function ProgressBar({percent}) {
  return (
    <div
      className="bg-blue-500 p-1 rounded-md transition-all duration-500 ease-out font-mono text-white text-center text-xs min-w-8"
      style={{width: `${percent}%`}}
    >
      {Math.floor(percent).toFixed(0)}%
    </div>
  )
}

function userFilter(site, siteFilter, fundingFilter) {
  let network = true
  switch (siteFilter) {
    case 'sites_single':
      network = site.networkCount <= 1;
      break;
    case 'sites_network':
      network = site.networkCount > 1;
      break;
    case 'sites_any':
    default:
      network = true;
      break;
  }

  let funding = false
  switch (fundingFilter) {
    case 'funding_operational':
      funding = site.isActive;
      break;
    case 'funding_hold':
      funding = !site.isActive;
      break;
    case 'funding_any':
    default:
      funding = true;
      break;
  }
  return network && funding;
}

export default function SiteMap() {
  const {t} = useTranslation();

  const [visibleSites, setVisibleSites] = useState([]);
  const [bounds, setBounds] = useState({ne: {}, sw: {}})
  const [siteFilter, setSiteFilter] = useState('sites_any')
  const [fundingFilter, setFundingFilter] = useState('funding_any')
  const debug = typeof window === 'undefined' ? null : window.localStorage.getItem(DEBUG_KEY);


  const {
    isError,
    error,
    updatesTotal,
    updatesLoaded,
    updatesAvailable,
    allSites,
    filteredSites
  } =
    useSyncSitesToIndexedDb();


  useEffect(() => {
    // do not filter while we load data
    if (!updatesAvailable) {
      const visibleSites = filteredSites(bounds)
      // apply our filters
      const userFiltered = visibleSites.filter(site => userFilter(site, siteFilter, fundingFilter))
      setVisibleSites(userFiltered)
    }
  }, [allSites, updatesAvailable, bounds, siteFilter, fundingFilter])

  function handleFilterSite(filter) {
    console.log('change site', filter)
    if (['sites_any', 'sites_single', 'sites_network'].includes(filter)) {
      setSiteFilter(filter)
    }
  }

  function handleFilterFunding(filter) {
    if (['funding_any', 'funding_operational', 'funding_hold'].includes(filter)) {
      setFundingFilter(filter)
    }
  }

  function handleResetFilters() {
    setSiteFilter('sites_any')
    setFundingFilter('funding_any')
  }

  const showLoadingBar = updatesAvailable

  return (
    <div className="w-full max-w-6xl mx-auto p-4 space-y-4">

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <DropdownFilter title={t(`workspace.sales.siteMap.${siteFilter}`)} label={t('workspace.sales.siteMap.site_filter')}>
          <DropdownFilter.MenuItem
            title={t('any')}
            selected={siteFilter === 'sites_any'}
            onClick={() => handleFilterSite('sites_any')}
          />
          <DropdownFilter.MenuItem
            title={t('workspace.sales.siteMap.single')}
            selected={siteFilter === 'sites_single'}
            onClick={() => handleFilterSite('sites_single')}
          />
          <DropdownFilter.MenuItem
            title={t('network')}
            selected={siteFilter === 'sites_network'}
            onClick={() => handleFilterSite('sites_network')}
          />
        </DropdownFilter>

        <DropdownFilter title={t(`workspace.sales.siteMap.${fundingFilter}`)} label={t('workspace.sales.siteMap.funding_filter')}>
          <DropdownFilter.MenuItem
            title={t('any')}
            selected={fundingFilter === 'funding_any'}
            onClick={() => handleFilterFunding('funding_any')}
          />
          <DropdownFilter.MenuItem
            title={t('workspace.sales.siteMap.operational')}
            selected={fundingFilter === 'funding_operational'}
            onClick={() => handleFilterFunding('funding_operational')}
          />
          <DropdownFilter.MenuItem
            title={t('workspace.sales.siteMap.on_hold')}
            selected={fundingFilter === 'funding_hold'}
            onClick={() => handleFilterFunding('funding_hold')}
          />
        </DropdownFilter>

        <button className="text-sm test-base-content ml-auto underline"
                onClick={handleResetFilters}>
          {t('reset_filters')}
        </button>
      </div>
      {isError && <p>Error loading sites: {String(error)}</p>}

      {/* Loading indicator */}
      {debug && <div>
        Loading: {`${updatesAvailable}`} &nbsp;
        Updating {updatesTotal} sites. {updatesLoaded} / {updatesTotal} =
        {(updatesTotal > 0 ? updatesLoaded / updatesTotal * 100 : 0).toFixed(2)}%
      </div>}
      <AnimatePresence>
        {showLoadingBar && (
          <motion.div
            initial={{opacity: 0, y: 8}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: -8}}
            transition={{duration: 0.350}}
          >
            <div>
              Updating {updatesTotal} sites.
              <ProgressBar percent={updatesTotal > 0 ? updatesLoaded / updatesTotal * 100 : 0}/>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Map */}
      <div className="w-full h-[420px] flex items-center justify-center">
        <Map
          defaultZoom={4}
          defaultCenter={{lat: 39.8097, lng: -98.5556}}
          mapId={'3ec3b06668e7ee6e8be88cce'}
          zoomControl={false}
          mapTypeControl={false}
          fullscreenControl={true}
          streetViewControl={false}
          onIdle={(ev) => {
            const bounds = ev.map.getBounds();
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            setBounds({ne, sw})
          }}>
          {visibleSites.length > 0 && <ClusteredMarkerLayer visibleSites={visibleSites}/>}
        </Map>
      </div>
      <div className="text-xs !-mt-0.5">{visibleSites?.length} of {allSites?.length}</div>

      {/* Site data */}
      <SiteEngagements/>
    </div>
  )
}
